import { useState, useEffect } from 'react';
import { SurahReader } from './SurahReader';
import { Surah } from '../data/quran';

interface QuranLoaderProps {
  surahId: number;
  onBack: () => void;
  onComplete: () => void;
}

export function QuranLoader({ surahId, onBack, onComplete }: QuranLoaderProps) {
  const [surah, setSurah] = useState<Surah | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Fetch from API
    // Using http://api.alquran.cloud/v1/surah/{id}/editions/quran-uthmani,en.sahih,en.transliteration
    // This is a bit complex to map perfectly to our structure, so let's simplify.
    // We will fetch: Arabic, Translation (Sahih International), Transliteration
    
    const fetchSurah = async () => {
      try {
        const res = await fetch(`https://api.alquran.cloud/v1/surah/${surahId}/editions/quran-uthmani,en.sahih,en.transliteration`);
        const data = await res.json();
        
        if (data.code === 200 && data.data.length === 3) {
            const arabicData = data.data[0];
            const translationData = data.data[1];
            const transliterationData = data.data[2];

            const verses = arabicData.ayahs.map((ayah: any, index: number) => ({
                id: index + 1,
                arabic: ayah.text,
                translation: translationData.ayahs[index].text,
                transliteration: transliterationData.ayahs[index].text
            }));

            const mappedSurah: Surah = {
                id: surahId,
                name: arabicData.name,
                englishName: arabicData.englishName,
                meaning: arabicData.englishNameTranslation,
                verses: verses,
                // Audio URL format: https://server8.mp3quran.net/afs/{padded_id}.mp3
                audioUrl: `https://server8.mp3quran.net/afs/${surahId.toString().padStart(3, '0')}.mp3`
            };
            
            setSurah(mappedSurah);
        } else {
            throw new Error('Invalid data format');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load Surah. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    fetchSurah();
  }, [surahId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center flex-col gap-4">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-emerald-700 font-medium animate-pulse">Opening the Book...</p>
      </div>
    );
  }

  if (error || !surah) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center flex-col p-6 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          onClick={onBack} 
          className="bg-slate-200 px-6 py-3 rounded-xl font-bold text-slate-700 hover:bg-slate-300 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return <SurahReader surah={surah} onBack={onBack} onComplete={onComplete} />;
}
